<template>
  <modal
    :show="internalShow"
    :centered="true"
    :closeOnOutsideClick="false"
    modalClasses="modal-lg"
    headerClasses="justify-content-center"
    @close="handleClose"
  >
    <template slot="header">
      <h4 class="title title-up">Confirm Your Subscription</h4>
    </template>

    <div class="subscription-modal-body">
      <p class="subscription-intro">
        You are subscribing to the following Module(s):
      </p>

      <!-- Module List with Checkboxes -->
      <div 
        v-for="(module, index) in modules" 
        :key="index" 
        class="module-card"
        :class="{ 'module-selected': selectedModules.includes(index) }"
      >
        <div class="module-header">
          <div class="module-checkbox-wrapper">
            <input
              type="checkbox"
              :id="`module-${index}`"
              :checked="selectedModules.includes(index)"
              @change="toggleModule(index)"
              class="module-checkbox"
            />
            <label :for="`module-${index}`" class="module-name">
              {{ module.name }}
            </label>
          </div>
          <span class="module-hint">(Each Module should be listed individually with the info below for each)</span>
        </div>

        <!-- Module Details (always shown) -->
        <div class="module-details">
          <div class="subscription-info">
            <span class="info-item">
              <strong>Monthly Fee:</strong> {{ formatCurrency(module.monthlyFee) }}
            </span>
            <span class="info-item">
              <strong>Monthly Allocation:</strong> {{ module.monthlyAllocation }}
            </span>
            <span class="info-item">
              <strong>Overage Billing:</strong> {{ module.overageBilling ? 'Enabled' : 'Disabled' }}
            </span>
            <span class="info-item">
              <strong>Overage Rate:</strong> {{ module.overageBilling ? formatCurrency(module.overageRate) : 'N/A' }}
            </span>
            <span class="info-item">
              <strong>Commitment Term:</strong> {{ module.commitmentTerm }} months
            </span>
            <span class="info-item">
              <strong>Billing Frequency:</strong> {{ module.billingFrequency }}
            </span>
            <span class="info-item">
              <strong>Effective Date:</strong> {{ formatDate(module.effectiveDate) }}
            </span>
            <span class="info-item">
              <strong>First Billing Date:</strong> {{ formatDate(module.firstBillingDate) }}
            </span>
            <span class="info-item">
              <strong>Annual Commitment Total:</strong> {{ formatCurrency(module.annualCommitmentTotal) }}
            </span>
          </div>
        </div>
      </div>

      <!-- Agreement Text -->
      <div class="agreement-section">
        <p class="agreement-intro">
          By clicking "Confirm Subscription" below, you acknowledge and agree that:
        </p>
        
        <ul class="agreement-list">
          <li>
            You have reviewed and agree to the Subscription Terms displayed above.
          </li>
          <li>
            These Subscription Terms are incorporated into and governed by your Master Services Agreement with Exact Match Marketing Inc.
          </li>
          <li>
            This subscription requires a 12-month commitment. Early termination does not relieve you of your obligation to pay the remaining monthly fees for the balance of the commitment term.
          </li>
          <li>
            Your payment method on file will be charged the Monthly Fee shown above on a recurring monthly basis beginning on the First Billing Date.
          </li>
          <li>
            Exact Match will retain a record of these Subscription Terms, which you may access at any time in your account settings.
          </li>
        </ul>
      </div>
    </div>

    <template slot="footer">
      <div class="modal-footer-content">
        <base-button
          simple
          @click.native="handleCancel"
          :disabled="internalIsLoading"
          class="footer-button-cancel"
        >
          Cancel
        </base-button>
        <base-button
          type="primary"
          @click.native="handleConfirm"
          :disabled="selectedModules.length === 0 || internalIsLoading"
          class="footer-button-confirm"
        >
          <i v-if="internalIsLoading" class="fas fa-spinner fa-spin mr-2"></i>
          Confirm Subscription
        </base-button>
      </div>
    </template>
  </modal>
</template>

<script>
import { Modal, BaseButton } from '@/components';

export default {
  name: 'SubscriptionConfirmationModal',
  components: {
    Modal,
    BaseButton
  },
  props: {
    // Hanya modules yang tetap sebagai prop karena data berasal dari parent
    modules: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      selectedModules: [],
      // Internal state untuk show (proxy untuk prop)
      internalShow: false,
      internalIsLoading: false,
    };
  },
  watch: {
    // Watch internalShow untuk trigger ketika modal dibuka
    internalShow(newVal) {
      if (newVal && this.modules.length > 0) {
        // Select all modules by default when modal opens
        // Tambahkan check modules.length untuk safety
        this.selectedModules = this.modules.map((_, index) => index);
      }
    }
  },
  methods: {
    toggleModule(index) {
      const idx = this.selectedModules.indexOf(index);
      if (idx > -1) {
        this.selectedModules.splice(idx, 1);
      } else {
        this.selectedModules.push(index);
      }
    },
    formatCurrency(value) {
      if (value === null || value === undefined) return 'N/A';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(value);
    },
    formatDate(date) {
      if (!date) return 'N/A';
      const d = new Date(date);
      return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },
    handleClose() {
      // Handle close dari modal (klik X atau outside click)
      this.$emit('cancel');
    },
    handleCancel() {
      // Handle cancel button
      this.$emit('cancel');
    },
    handleConfirm() {
      if (this.selectedModules.length === 0 || this.internalIsLoading) {
        return;
      }
      
      // Emit selected modules only
      const selectedModulesData = this.selectedModules.map(idx => this.modules[idx]);
      console.log('handleConfirm: selectedModulesData', { selectedModulesData })
      this.$emit('confirm', selectedModulesData);
      // Don't close modal here, let parent handle it after processing
    }
  }
};
</script>

<style scoped>
.subscription-modal-body {
  padding: 24px 32px 24px 32px;
  max-height: 70vh;
  overflow-y: auto;
}

.subscription-intro {
  font-size: 15px;
  font-weight: 500;
  margin-bottom: 24px;
  color: #525f7f;
  line-height: 1.5;
}

/* Module Card */
.module-card {
  margin-bottom: 16px;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  background: #ffffff;
  transition: all 0.2s ease;
  overflow: hidden;
}

.module-card:hover {
  border-color: #dee2e6;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.module-card.module-selected {
  border-color: #5e72e4;
  background: #f8f9ff;
}

.module-header {
  padding: 20px 24px 16px 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.module-checkbox-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
}

.module-checkbox {
  width: 20px;
  height: 20px;
  cursor: pointer;
  accent-color: #5e72e4;
  flex-shrink: 0;
}

.module-name {
  font-size: 16px;
  font-weight: 600;
  color: #212529;
  cursor: pointer;
  margin: 0;
  user-select: none;
}

.module-hint {
  font-size: 12px;
  color: #8898aa;
  font-style: italic;
  margin-left: 32px;
}

.module-details {
  padding: 0 24px 20px 24px;
  border-top: 1px solid #e9ecef;
  margin-top: 0;
  padding-top: 16px;
}

.subscription-info {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 12px 20px;
  font-size: 14px;
  color: #525f7f;
  line-height: 1.6;
  align-items: flex-start;
  width: 100%;
}

.info-item {
  display: inline-block;
  white-space: nowrap;
  flex-shrink: 0;
}

.info-item strong {
  color: #212529;
  font-weight: 600;
  margin-right: 4px;
}

/* Agreement Section */
.agreement-section {
  margin-top: 32px;
  padding-top: 24px;
  border-top: 2px solid #e9ecef;
}

.agreement-intro {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #212529;
  line-height: 1.5;
}

.agreement-list {
  list-style: none;
  padding-left: 0;
  margin-bottom: 0;
}

.agreement-list li {
  padding: 10px 0;
  padding-left: 24px;
  position: relative;
  color: #525f7f;
  line-height: 1.7;
  font-size: 14px;
}

.agreement-list li:before {
  content: "•";
  position: absolute;
  left: 8px;
  color: #5e72e4;
  font-weight: bold;
  font-size: 18px;
  line-height: 1.4;
}

.modal-lg {
  max-width: 900px;
}

/* Footer Styles */
::v-deep .modal-footer {
  border-top: 1px solid #e9ecef;
  background-color: #f8f9fa;
  padding: 20px 32px;
  margin-top: 0;
}

.modal-footer-content {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  padding: 0;
  width: 100%;
}

.footer-button-cancel {
  min-width: 100px;
  padding: 10px 24px;
  font-weight: 500;
  background-color: #ffffff;
  color: #525f7f;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.footer-button-cancel:hover {
  background-color: #f7fafc;
  border-color: #cbd5e0;
  color: #32325d;
}

.footer-button-confirm {
  min-width: 160px;
  padding: 10px 24px;
  font-weight: 600;
  background-color: #212529;
  color: #ffffff;
  border: 1px solid #212529;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.footer-button-confirm:hover:not(:disabled) {
  background-color: #32325d;
  border-color: #32325d;
}

.footer-button-confirm:disabled {
  background-color: #cbd5e0;
  border-color: #cbd5e0;
  color: #8898aa;
  cursor: not-allowed;
  opacity: 0.6;
}

/* Custom scrollbar */
.subscription-modal-body::-webkit-scrollbar {
  width: 6px;
}

.subscription-modal-body::-webkit-scrollbar-track {
  background: #f1f3f5;
  border-radius: 3px;
}

.subscription-modal-body::-webkit-scrollbar-thumb {
  background: #cbd5e0;
  border-radius: 3px;
}

.subscription-modal-body::-webkit-scrollbar-thumb:hover {
  background: #a0aec0;
}
</style>
